import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { AdminApi, AppInstanceSummary, AppPushCapabilities } from '../../../api/client.ts';

/** The admin API from the router context (pages and components alike). */
export const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

/** Query keys shared with the push composer (`usePushRegistry`), so an edit here refreshes it too. */
export const pushKeys = {
  settings: (env: string) => ['push-settings', env],
  channels: (env: string) => ['push-android-channels', env],
  categories: (env: string) => ['push-ios-categories', env],
  threads: (env: string) => ['push-ios-threads', env],
  apps: (env: string) => ['apps', env],
  /** Prefix of every capabilities query of the environment (the composer keys them by the list of iOS apps). */
  capabilities: (env: string) => ['push-capabilities', env],
  capabilitiesOf: (env: string, appId: string) => ['push-capabilities', env, appId],
  credential: (env: string, appId: string) => ['push-credential', env, appId],
  sdkConfigs: (env: string) => ['push-sdk-config', env],
  sdkConfig: (env: string, appId: string) => ['push-sdk-config', env, appId],
};

/** Active apps that get FCM / APNs push (web push is deferred, BB-83). */
export const pushApps = (apps: AppInstanceSummary[] | undefined): AppInstanceSummary[] => (apps ?? []).filter((a) => a.status === 'active' && (a.platform === 'android' || a.platform === 'ios'));

/**
 * What the push settings page reads (BB-77): the environment's defaults, the channel / category / thread registry, the
 * apps and what each app declares. Every section shows its own loading and error state.
 */
export function usePushSettingsData(environmentId: string) {
  const api = useApi();
  const settings = useQuery({ queryKey: pushKeys.settings(environmentId), queryFn: () => api.pushSettings(environmentId) });
  const channels = useQuery({ queryKey: pushKeys.channels(environmentId), queryFn: () => api.pushAndroidChannels(environmentId) });
  const categories = useQuery({ queryKey: pushKeys.categories(environmentId), queryFn: () => api.pushIosCategories(environmentId) });
  const threads = useQuery({ queryKey: pushKeys.threads(environmentId), queryFn: () => api.pushIosThreads(environmentId) });
  const apps = useQuery({ queryKey: pushKeys.apps(environmentId), queryFn: () => api.listApps(environmentId) });
  const appsWithPush = pushApps(apps.data);
  const iosApps = appsWithPush.filter((a) => a.platform === 'ios');
  const capabilities = useQuery({
    queryKey: [...pushKeys.capabilities(environmentId), 'page', iosApps.map((a) => a.id)],
    enabled: apps.isSuccess,
    queryFn: async () => Object.fromEntries(await Promise.all(iosApps.map(async (a) => [a.id, await api.appPushCapabilities(environmentId, a.id)] as const))) as Record<string, AppPushCapabilities>,
  });
  return {
    settings,
    channels,
    categories,
    threads,
    apps,
    appsWithPush,
    /** Whether some iOS app declares the time-sensitive entitlement; `undefined` while not known. */
    timeSensitiveAvailable: capabilities.data ? Object.values(capabilities.data).some((c) => c.iosTimeSensitiveEnabled) : undefined,
  };
}

export type PushSettingsPageData = ReturnType<typeof usePushSettingsData>;
